import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const REFRESH_MS = 2 * 60 * 1000; // Refresh every 2 minutes

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function timeAgo(ts) {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
}

export default function Location({ kidId, parentId, deviceFilter }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const loadLocations = async () => {
    if (!parentId || deviceFilter === 'mac') return;
    setLoading(true);
    setProgress(20);

    // Get today's locations
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let query = supabase
      .from('location_history')
      .select('*')
      .eq('parent_id', parentId)
      .gte('timestamp', today.toISOString())
      .order('timestamp', { ascending: false })
      .limit(100);

    if (kidId) query = query.eq('kid_id', kidId);

    setProgress(50);
    const { data, error } = await query;
    setProgress(90);

    if (error) console.error('Location fetch error:', error);
    setLocations(data || []);
    setProgress(100);
    setLoading(false);
  };

  useEffect(() => {
    loadLocations();
    const interval = setInterval(loadLocations, REFRESH_MS);
    return () => clearInterval(interval);
  }, [parentId, kidId, deviceFilter]); // eslint-disable-next-line react-hooks/exhaustive-deps

  const latest = locations[0];
  const mapUrl = latest
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${latest.longitude - 0.005},${latest.latitude - 0.003},${latest.longitude + 0.005},${latest.latitude + 0.003}&layer=mapnik&marker=${latest.latitude},${latest.longitude}`
    : null;

  const mapsLink = latest
    ? `https://www.google.com/maps?q=${latest.latitude},${latest.longitude}`
    : null;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-icon">{'\uD83D\uDCCD'}</div>
        <div className="loading-title">Loading location...</div>
        <div className="loading-bar-track">
          <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="loading-percent">{progress}%</div>
      </div>
    );
  }

  if (deviceFilter === 'mac') {
    return (
      <div>
        <div className="page-header">
          <h1 className="page-title">{'\uD83D\uDCCD'} Location</h1>
        </div>
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83D\uDDA5\uFE0F'}</div>
          <h3 style={{ marginBottom: 8 }}>Location is phone-only</h3>
          <p style={{ fontSize: 14, color: '#64748b' }}>
            Switch to <strong>Phone</strong> or <strong>All</strong> to see location data.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          {'\uD83D\uDCCD'} Location
        </h1>
        <button className="btn-sm" onClick={loadLocations} style={{
          background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
          border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8,
          padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>
          Refresh
        </button>
      </div>

      {!latest ? (
        <div style={{
          background: '#1e293b', borderRadius: 16, padding: 40,
          textAlign: 'center', color: '#64748b',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>{'\uD83D\uDCF1'}</div>
          <h3 style={{ color: '#e2e8f0', marginBottom: 8 }}>No location data yet</h3>
          <p style={{ fontSize: 14 }}>
            Location tracking starts when the Android app is installed and has location permission granted.
          </p>
        </div>
      ) : (
        <>
          {/* Map */}
          <div style={{
            borderRadius: 16, overflow: 'hidden', marginBottom: 20,
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <iframe
              title="Kid Location"
              src={mapUrl}
              width="100%"
              height="350"
              style={{ border: 0, display: 'block' }}
            />
          </div>

          {/* Current location card */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, margin: 0 }}>
                {'\uD83D\uDFE2'} Last Known Location
              </h3>
              <span style={{ color: '#64748b', fontSize: 12 }}>{timeAgo(latest.timestamp)}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>COORDINATES</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>
                  {latest.latitude.toFixed(6)}, {latest.longitude.toFixed(6)}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>ACCURACY</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>
                  {latest.accuracy ? `${Math.round(latest.accuracy)}m` : 'N/A'}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>SPEED</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>
                  {latest.speed ? `${(latest.speed * 3.6).toFixed(1)} km/h` : 'Stationary'}
                </div>
              </div>
              <div>
                <div style={{ color: '#64748b', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>BATTERY</div>
                <div style={{ color: '#e2e8f0', fontSize: 14 }}>
                  {latest.battery ? `${latest.battery}%` : 'N/A'}
                </div>
              </div>
            </div>

            <a
              href={mapsLink}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block', marginTop: 16, padding: '8px 16px',
                background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                textDecoration: 'none', border: '1px solid rgba(59,130,246,0.3)',
              }}
            >
              Open in Google Maps
            </a>
          </div>

          {/* Location history */}
          <div className="card">
            <h3 style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
              Today's History ({locations.length} points)
            </h3>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {locations.map((loc, i) => (
                <div key={loc.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: i < locations.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                }}>
                  <div>
                    <span style={{ color: '#e2e8f0', fontSize: 13 }}>
                      {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                    </span>
                    {loc.accuracy && (
                      <span style={{ color: '#64748b', fontSize: 11, marginLeft: 8 }}>
                        {'\u00B1'}{Math.round(loc.accuracy)}m
                      </span>
                    )}
                  </div>
                  <span style={{ color: '#64748b', fontSize: 12, flexShrink: 0 }}>
                    {formatTime(loc.timestamp).split(', ')[1] || formatTime(loc.timestamp)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
